import { faArrowRightArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Action,
  Badge,
  Card,
  Feed,
  Icon,
  Modal,
  Skeleton,
} from 'corso-admin-ui';
import { CrewMerchantUi, crewClaimRollupStatusCodeName } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { useState } from 'react';
import { useConciergeCustomer } from '~/hooks/useConciergeCustomer';
import { useEnabledClaimType } from '~/hooks/useEnabledClaimType';
import { useStoreCustomer } from '~/hooks/useStoreCustomer';
import { shippingClaimStatusName } from '~/utils/enumNameMaps';

type CustomerClaimHistory = Pick<
  CrewMerchantUi.StoreCustomer,
  'claims' | 'shippingClaims'
>;

type SharedClaim = {
  id: string;
  createdOn: string;
  type: 'Return' | 'Warranty' | 'Shipping';
  status: string;
  label: string;
  to: string | null;
  storeName: string | null;
};

const sharedClaimIcon: Record<SharedClaim['type'], React.ReactNode> = {
  Return: <FontAwesomeIcon icon={faArrowRightArrowLeft} />,
  Warranty: <Icon icon="verified" />,
  Shipping: <Icon icon="productGroup" />,
} as const;

function OverviewCard({
  heading,
  content,
}: {
  heading: string;
  content: string;
}) {
  return (
    <Card>
      <div className="flex flex-col items-center justify-center space-y-2">
        <Card.Heading>{heading}</Card.Heading>
        <p className="text-neutral-500">{content}</p>
      </div>
    </Card>
  );
}

export function CustomerClaimDisplay({
  customer,
  customerName,
  claimEmail,
  show,
  claimId,
  onClose,
  crossStore = false,
}: {
  customer: CustomerClaimHistory;
  claimEmail?: string;
  customerName?: string;
  show: boolean;
  claimId?: number;
  onClose: () => void;
  /** history spans every store, so name each claim's store and count every claim type */
  crossStore?: boolean;
}) {
  const {
    isLoading,
    isReturnsEnabled,
    isWarrantyEnabled,
    isShippingProtectionEnabled,
  } = useEnabledClaimType();

  const { shippingClaims } = customer;
  const returns = customer.claims.filter((c) => c.claimType === 'Return');
  const warranties = customer.claims.filter((c) => c.claimType === 'Warranty');

  const allClaims = [...returns, ...warranties, ...shippingClaims].map((c) => {
    const storeName = crossStore ? c.storeName : null;
    if ('claimType' in c) {
      return {
        id: `${c.claimType}-${c.id}`,
        label: `#${c.externalId}`,
        createdOn: c.createdOn,
        type: c.claimType === 'Return' ? 'Return' : 'Warranty',
        status: crewClaimRollupStatusCodeName[c.claimRollupStatusCode],
        to:
          c.id === claimId
            ? null
            : `/${c.storeId}/claims/${c.claimType === 'Return' ? 'return' : 'warranty'}/${c.id}`,
        storeName,
      };
    }
    return {
      id: `Shipping-${c.id}`,
      label: `#${c.id}`,
      createdOn: c.createdOn,
      type: 'Shipping',
      status: shippingClaimStatusName[c.status],
      to: c.id === claimId ? null : `/${c.storeId}/claims/shipping/${c.id}`,
      storeName,
    };
  }) satisfies SharedClaim[];

  if (!crossStore && isLoading) return null;
  return (
    <Modal
      title={customerName ?? 'Customer Claims'}
      show={show}
      onClose={onClose}
    >
      <p className="text-sm">{claimEmail}</p>
      <div className="flex justify-evenly gap-4">
        {(crossStore || isReturnsEnabled) && (
          <OverviewCard heading="Returns" content={returns.length.toString()} />
        )}

        {(crossStore || isWarrantyEnabled) && (
          <OverviewCard
            heading="Warranties"
            content={warranties.length.toString()}
          />
        )}

        {(crossStore || isShippingProtectionEnabled) && (
          <OverviewCard
            heading="Shipping"
            content={shippingClaims.length.toString()}
          />
        )}
      </div>

      <Feed
        events={allClaims.sort(
          (a, b) =>
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
        )}
        renderIcon={(event) => sharedClaimIcon[event.type]}
      >
        {(event) => (
          <div className="flex flex-auto flex-col gap-1">
            <div className="flex flex-wrap items-center justify-between">
              <div className="flex flex-col gap-2">
                {event.storeName && (
                  <p className="text-xs font-medium">{event.storeName}</p>
                )}
                <span className="flex gap-2">
                  {event.to ? (
                    <Action variant="link" to={event.to}>
                      <p className="font-semibold">{event.label}</p>
                    </Action>
                  ) : (
                    <p className="text-sm font-semibold">{event.label}</p>
                  )}
                  <Badge>{event.status}</Badge>
                </span>

                <span className="text-sm text-neutral-500">{event.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs leading-5 text-neutral-500">
                  {formatter.relativeTimeFromNow(new Date(event.createdOn))}
                </span>
              </div>
            </div>
          </div>
        )}
      </Feed>
    </Modal>
  );
}

function CustomerClaimSummary({
  customer,
  isPending,
  customerName,
  claimEmail,
  claimId,
  crossStore,
}: {
  customer: CustomerClaimHistory | undefined;
  isPending: boolean;
  claimId: number;
  customerName?: string;
  claimEmail: string;
  crossStore?: boolean;
}) {
  const [showCustomerClaims, setShowCustomerClaims] = useState(false);

  if (isPending) return <Skeleton.Rectangle width="25%" height="16px" />;

  if (!customer) return null;

  const totalClaims = customer.claims.length + customer.shippingClaims.length;

  if (totalClaims === 0) return null;

  return (
    <div>
      <Action variant="link" onClick={() => setShowCustomerClaims(true)}>
        <p className="text-xs">
          {totalClaims} {totalClaims === 1 ? 'claim' : 'claims'}
        </p>
      </Action>

      <CustomerClaimDisplay
        customerName={customerName}
        customer={customer}
        show={showCustomerClaims}
        onClose={() => setShowCustomerClaims(false)}
        claimEmail={claimEmail}
        claimId={claimId}
        crossStore={crossStore}
      />
    </div>
  );
}

type OverviewProps = {
  claimId: number;
  customerName?: string;
  claimEmail: string;
};

/** The active store's customer record. */
export function StoreCustomerClaimOverview({
  customerName,
  claimEmail,
  claimId,
}: OverviewProps) {
  const { data: storeCustomer, isPending } = useStoreCustomer(claimEmail);

  const bestName =
    storeCustomer?.firstName && storeCustomer.lastName
      ? `${storeCustomer.firstName} ${storeCustomer.lastName}`
      : customerName;

  return (
    <CustomerClaimSummary
      customer={storeCustomer}
      isPending={isPending}
      customerName={bestName}
      claimEmail={claimEmail}
      claimId={claimId}
    />
  );
}

/** Claim history across every store, for Corso CX agents. The name comes from the current claim. */
export function ConciergeCustomerClaimOverview({
  customerName,
  claimEmail,
  claimId,
}: OverviewProps) {
  const { data: customer, isPending } = useConciergeCustomer(claimEmail);

  return (
    <CustomerClaimSummary
      customer={customer}
      isPending={isPending}
      customerName={customerName}
      claimEmail={claimEmail}
      claimId={claimId}
      crossStore
    />
  );
}
